import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown, Label, Spinner } from "@heroui/react";
import {
  api,
  RecipeListItem,
  WeekDto,
  WeekSlotDto,
  SlotCard,
  AppUser,
} from "../api/client";
import { RecipeModal } from "../components/RecipeModal";
import { RecipePickerSheet } from "../components/RecipePickerSheet";
import { Thumb } from "../components/Thumb";
import { useAuth } from "../auth";
import {
  SLOT_ROWS,
  DAY_LABELS_FULL,
  MONTHS_CA,
  isoMondayString,
  isUserActiveOnSlot,
} from "../constants";
import type { MealSlot } from "../api/client";

// A partir d'aquesta hora (després de sopar) la pantalla "Avui" salta al dia
// següent: ja has sopat, així que el que toca és veure el pla de demà.
const NIGHT_ROLLOVER_HOUR = 22;

// Data de referència de la pantalla. Normalment "ara"; després de sopar,
// demà (isNight = true). Es calcula amb data local, així el salt de diumenge
// nit a dilluns també canvia de setmana correctament.
function getReferenceDate(): { date: Date; isNight: boolean } {
  const now = new Date();
  const isNight = now.getHours() >= NIGHT_ROLLOVER_HOUR;
  const date = new Date(now);
  if (isNight) date.setDate(date.getDate() + 1);
  return { date, isNight };
}

function weekdayOf(d: Date): number {
  const day = d.getDay();
  return day === 0 ? 7 : day;
}

function getNextMealSlot(isNight: boolean): MealSlot {
  if (isNight) return "breakfast1";
  const hour = new Date().getHours();
  if (hour < 9) return "breakfast1";
  if (hour < 14) return "lunch";
  if (hour < 17) return "snack";
  return "dinner";
}

function getMealTime(slot: MealSlot): string {
  const times: Record<MealSlot, string> = {
    breakfast1: "09:00",
    breakfast2: "08:00",
    lunch: "14:00",
    snack: "17:00",
    dinner: "20:00",
  };
  return times[slot] || "—";
}

function greetingForHour(hour: number, isNight: boolean): string {
  if (isNight) return "Bona nit";
  if (hour < 14) return "Bon dia";
  if (hour < 21) return "Bona tarda";
  return "Bon vespre";
}

interface TodayMeal {
  slot: MealSlot;
  label: string;
  time: string;
  card: WeekSlotDto | null;
}

// Accions compartides per les targetes de recepta.
interface MealHandlers {
  onOpenRecipe: (recipeId: number, servings: number, cooking?: boolean) => void;
  onViewRecipe: (recipeId: number) => void;
  onEditRecipe: (recipeId: number) => void;
  onDeleteCard: (cardId: number) => void;
  onToggleUser: (card: SlotCard, userId: number) => void;
  onAddRecipe: (slot: WeekSlotDto) => void;
}

// Detecta pantalla petita (mòbil). En aquesta mida, clicar una recepta obre
// directament el mode "Cuina ara" en lloc de la vista de recepta.
function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

export function Today() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  // Referència de la pantalla: ara, o demà si ja és de nit (després de sopar).
  const [{ refDate, isNight }] = useState(() => {
    const r = getReferenceDate();
    return { refDate: r.date, isNight: r.isNight };
  });
  // El dilluns de la setmana es fixa en muntar a partir de la referència
  // (així una nit de diumenge ja mostra la setmana següent).
  const [monday] = useState(() => isoMondayString(refDate));
  const [week, setWeek] = useState<WeekDto | null>(null);
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [openRecipeId, setOpenRecipeId] = useState<number | null>(null);
  const [openServings, setOpenServings] = useState(1);
  const [startCooking, setStartCooking] = useState(false);
  const [pickerSlot, setPickerSlot] = useState<WeekSlotDto | null>(null);

  useEffect(() => {
    api.get<AppUser[]>("/users").then(setUsers);
    api.get<RecipeListItem[]>("/recipes?subrecipe=false").then(setRecipes);
  }, []);

  useEffect(() => {
    api.get<WeekDto>(`/weeks/${monday}`).then(setWeek);
  }, [monday]);

  const today = useMemo(() => weekdayOf(refDate), [refDate]);
  const nextMealSlot = useMemo(() => getNextMealSlot(isNight), [isNight]);

  const todayMeals = useMemo<TodayMeal[]>(() => {
    if (!week) return [];
    return SLOT_ROWS.map((row) => {
      const card =
        week.slots.find((s) => s.day === today && s.mealSlot === row.slot) || null;
      return { slot: row.slot, label: row.label, time: getMealTime(row.slot), card };
    });
  }, [week, today]);

  // Franja "actual" segons l'hora (per a l'estat de la timeline).
  const nowIndex = useMemo(() => {
    const idx = SLOT_ROWS.findIndex((r) => r.slot === nextMealSlot);
    return idx >= 0 ? idx : 0;
  }, [nextMealSlot]);

  // Franja seleccionada (clicant la timeline). Per defecte, l'actual.
  const [selIndex, setSelIndex] = useState(nowIndex);
  useEffect(() => setSelIndex(nowIndex), [nowIndex]);

  async function refreshWeek() {
    const w = await api.get<WeekDto>(`/weeks/${monday}`);
    setWeek(w);
  }

  function openRecipe(recipeId: number, servings: number, cooking = false) {
    setOpenRecipeId(recipeId);
    setOpenServings(servings);
    setStartCooking(cooking);
  }

  function viewRecipe(recipeId: number) {
    nav(`/receptes/${recipeId}`);
  }

  function editRecipe(recipeId: number) {
    nav(`/receptes/${recipeId}/edita`);
  }

  async function toggleUser(card: SlotCard, userId: number) {
    const ids = card.users.map((u) => u.userId);
    const next = ids.includes(userId)
      ? ids.filter((id) => id !== userId)
      : [...ids, userId];
    try {
      await api.patch(`/slot-cards/${card.id}`, { userIds: next });
      await refreshWeek();
    } catch (err) {
      console.error("Error updating card users:", err);
    }
  }

  async function deleteCard(cardId: number) {
    try {
      await api.del(`/slot-cards/${cardId}`);
      await refreshWeek();
    } catch (err) {
      console.error("Error deleting card:", err);
    }
  }

  // Afegeix una recepta a un slot amb els usuaris actius per defecte d'aquella
  // franja. Mateixa lògica que el tauler setmanal (WeekBoard).
  async function addRecipeToSlot(slot: WeekSlotDto, recipeId: number) {
    const defaultUserIds = users
      .filter((u) =>
        isUserActiveOnSlot(
          u.slotDefaults ?? [],
          u.slotExceptions ?? [],
          slot.day,
          slot.mealSlot
        )
      )
      .map((u) => u.id);
    await api.post(`/weeks/${monday}/slots/${slot.id}/cards`, {
      recipeId,
      userIds: defaultUserIds,
    });
    await refreshWeek();
  }

  const handlers: MealHandlers = {
    onOpenRecipe: openRecipe,
    onViewRecipe: viewRecipe,
    onEditRecipe: editRecipe,
    onDeleteCard: deleteCard,
    onToggleUser: toggleUser,
    onAddRecipe: setPickerSlot,
  };

  if (!week) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const meal = todayMeals[selIndex] ?? todayMeals[0];
  const slot = meal?.card ?? null;
  const cards = slot?.cards ?? [];

  const firstName = (user?.name ?? "").split(" ")[0];
  const greeting = greetingForHour(new Date().getHours(), isNight);
  const dateStr = `${DAY_LABELS_FULL[today]}, ${refDate.getDate()} de ${MONTHS_CA[refDate.getMonth()]}`;

  return (
    <div className="today-screen">
      <div className="page-head">
        <h1 className="flex items-center gap-2.5 whitespace-nowrap text-3xl">
          {greeting}
          {firstName ? `, ${firstName}` : ""} <span className="text-[26px]">👋</span>
        </h1>
        <div className="mt-1.5 font-semibold text-muted">{dateStr}</div>
      </div>

      <div className="today-grid">
        {/* COLUMNA PRINCIPAL — receptes de la franja seleccionada */}
        <div className="flex flex-col gap-3.5">
          <div className="flex items-baseline gap-2.5">
            <h2 className="text-[22px]">{meal.label}</h2>
            <span className="font-bold text-muted">{meal.time}</span>
          </div>

          <div className="hero-list">
            {cards.map((card) => (
              <HeroCard
                key={card.id}
                card={card}
                users={users}
                handlers={handlers}
                isMobile={isMobile}
              />
            ))}
            {slot && (
              <button
                type="button"
                className="add-card"
                onClick={() => handlers.onAddRecipe(slot)}
              >
                <span className="add-plus">
                  <PlusIcon />
                </span>
                <b>Afegeix recepta</b>
                <span className="text-[13px] text-muted">per {meal.label.toLowerCase()}</span>
              </button>
            )}
          </div>
        </div>

        {/* TIMELINE — el teu dia */}
        <aside>
          <div className="section-title mb-3">El teu dia</div>
          <div className="timeline">
            {todayMeals.map((m, i) => {
              const cards = m.card?.cards ?? [];
              const state = i < nowIndex ? "done" : i === nowIndex ? "now" : "next";
              const active = i === selIndex;
              return (
                <button
                  key={m.slot}
                  type="button"
                  className={`tl ${state} ${active ? "active" : ""}`}
                  onClick={() => setSelIndex(i)}
                >
                  <span className="tl-rail">
                    <span className="tl-dot" />
                  </span>
                  <span className="tl-body">
                    <span className="tl-time">
                      {m.time} · {m.label}
                    </span>
                    {cards.length > 0 ? (
                      <span className="tl-recipes">
                        {cards.slice(0, 2).map((c) => (
                          <span className="tl-recipe" key={c.id}>
                            <Thumb
                              name={c.recipe.name}
                              imageUrl={c.recipe.imageUrl}
                              meal={c.recipe.mealType}
                              size={13}
                              className="tl-thumb"
                            />
                            <span className="tl-name">{c.recipe.name}</span>
                          </span>
                        ))}
                        {cards.length > 2 && (
                          <span className="tl-more">+{cards.length - 2} més</span>
                        )}
                      </span>
                    ) : (
                      <span className="tl-empty">— lliure —</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </aside>
      </div>

      {openRecipeId && (
        <RecipeModal
          recipeId={openRecipeId}
          servings={openServings}
          onClose={() => {
            setOpenRecipeId(null);
            setStartCooking(false);
          }}
          startCooking={startCooking}
        />
      )}

      {pickerSlot && (
        <RecipePickerSheet
          slot={pickerSlot}
          recipes={recipes}
          onPick={async (recipeId) => {
            await addRecipeToSlot(pickerSlot, recipeId);
            setPickerSlot(null);
          }}
          onClose={() => setPickerSlot(null)}
        />
      )}

      <style>{`
        .today-screen .page-head{ margin-bottom:16px; }
        .today-screen .section-title{ font-family:var(--font-head); font-weight:600; font-size:13px;
          text-transform:uppercase; letter-spacing:.08em; color:var(--faint); }

        .today-grid{ display:grid; gap:24px; grid-template-columns:1fr; }
        @media(min-width:900px){ .today-grid{ grid-template-columns:minmax(0,1fr) 300px; } }
        .hero-list{ display:grid; gap:16px; grid-template-columns:1fr; align-items:stretch; }
        @media(min-width:560px){ .hero-list{ grid-template-columns:1fr 1fr; } }

        /* ---- HeroCard ---- */
        .hero-card{ overflow:hidden; display:flex; flex-direction:column; }
        .hero-media{ position:relative; }
        .hero-img-btn{ position:relative; display:block; width:100%; border:0; background:none; cursor:pointer;
          padding:0; text-align:left; border-radius:var(--radius-card) var(--radius-card) 0 0; overflow:hidden; }
        .hero-img{ width:100%; height:158px; }
        .hero-grad{ position:absolute; inset:0; pointer-events:none;
          background:linear-gradient(to top, rgba(0,0,0,.74), rgba(0,0,0,.05) 58%, transparent); }
        .hero-title{ position:absolute; left:0; right:54px; bottom:0; padding:13px 14px; color:#fff;
          font-size:18.5px; font-weight:600; text-shadow:0 2px 12px rgba(0,0,0,.45); line-height:1.14; }
        .hero-card:hover .hero-img img{ transform:scale(1.04); }
        .hero-img img{ transition:transform .5s; }

        .hero-actions{ position:absolute; top:10px; right:10px; display:flex; gap:7px; z-index:1; }

        .hero-foot{ padding:13px 14px; display:flex; align-items:center; justify-content:space-between; gap:12px 16px; flex-wrap:wrap; }
        .hero-meta{ display:inline-flex; align-items:center; gap:5px; color:var(--muted); font-weight:700; font-size:13px; }

        /* ---- add-recipe card ---- */
        .add-card{ display:flex; flex-direction:column; align-items:center; justify-content:center; gap:7px; text-align:center;
          min-height:170px; border:1.6px dashed var(--border-strong); border-radius:var(--radius-card); background:var(--surface-secondary);
          color:var(--muted); cursor:pointer; transition:.15s; font:inherit; }
        .add-card:hover{ border-color:var(--accent); color:var(--accent-soft-foreground);
          background:color-mix(in oklab, var(--accent-soft) 55%, var(--surface)); }
        .add-card b{ font-size:15px; color:var(--foreground); }
        .add-card:hover b{ color:var(--accent-soft-foreground); }
        .add-plus{ display:grid; place-items:center; width:42px; height:42px; border-radius:50%; background:var(--surface);
          border:1px solid var(--border-strong); color:var(--accent); box-shadow:var(--shadow-card-sm); }
        .add-card:hover .add-plus{ background:var(--accent); color:#fff; border-color:var(--accent); }

        /* ---- timeline ---- */
        .timeline{ display:flex; flex-direction:column; }
        .tl{ display:flex; gap:14px; text-align:left; border:0; background:transparent; cursor:pointer; padding:0; }
        .tl-rail{ position:relative; width:18px; flex:0 0 18px; display:flex; justify-content:center; }
        .tl-rail::before{ content:""; position:absolute; top:0; bottom:0; width:2px; background:var(--border-strong); }
        .tl:first-child .tl-rail::before{ top:18px; }
        .tl:last-child .tl-rail::before{ bottom:calc(100% - 18px); }
        .tl-dot{ position:relative; margin-top:10px; width:13px; height:13px; border-radius:50%; background:var(--surface);
          border:2.5px solid var(--border-strong); z-index:1; transition:.15s; }
        .tl.done .tl-dot{ background:var(--success); border-color:var(--success); }
        .tl.now .tl-dot{ background:var(--accent); border-color:var(--accent); box-shadow:0 0 0 4px var(--accent-soft); }
        .tl-body{ flex:1; display:flex; flex-direction:column; gap:6px; padding:8px 12px 16px; border-radius:14px; transition:.15s; }
        .tl:hover .tl-body{ background:var(--surface-secondary); }
        .tl.active .tl-body{ background:var(--surface); box-shadow:var(--shadow-card-sm); border:1px solid var(--border); }
        .tl-time{ font-weight:800; font-size:13px; color:var(--muted); }
        .tl.now .tl-time{ color:var(--accent-soft-foreground); }
        .tl.done{ opacity:.62; }
        .tl-recipes{ display:flex; flex-direction:column; gap:7px; }
        .tl-recipe{ display:flex; align-items:center; gap:8px; }
        .tl-thumb{ width:30px; height:30px; border-radius:8px; flex:0 0 30px; }
        .tl-name{ font-weight:700; font-size:13.5px; line-height:1.25; }
        .tl-more{ font-size:12px; color:var(--faint); font-weight:700; padding-left:38px; }
        .tl-empty{ font-size:13px; color:var(--faint); font-weight:600; font-style:italic; }

        /* ---- people row (emoji + punt de color) ---- */
        .people{ display:flex; align-items:flex-end; flex-wrap:wrap; gap:7px; line-height:1; font-size:20px; }
        .person{ display:flex; flex-direction:column; align-items:center; gap:3px; border:0; background:transparent;
          padding:0; cursor:pointer; font:inherit; transition:transform .12s; }
        .person:hover{ transform:translateY(-1px); }
        .person .pe{ line-height:1; transition:filter .12s, opacity .12s; }
        .person.off .pe{ filter:grayscale(1); opacity:.34; }
      `}</style>
    </div>
  );
}

function HeroCard({
  card,
  users,
  handlers,
  isMobile,
}: {
  card: SlotCard;
  users: AppUser[];
  handlers: MealHandlers;
  isMobile: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const servings = card.users.length || card.recipe.baseServings;
  const selected = card.users.map((u) => u.userId);

  function open(cooking = false) {
    handlers.onOpenRecipe(card.recipeId, servings, cooking);
  }

  // Clic a la targeta: al mòbil va directe a "Cuina ara"; a l'escriptori
  // obre el modal de vista de recepta.
  function openDefault() {
    open(isMobile);
  }

  function handleAction(key: string | number) {
    setMenuOpen(false);
    if (key === "cook") open(true);
    else if (key === "view") handlers.onViewRecipe(card.recipeId);
    else if (key === "edit") handlers.onEditRecipe(card.recipeId);
    else if (key === "remove") handlers.onDeleteCard(card.id);
  }

  return (
    <div className="hero-card rounded-card border border-border bg-surface shadow-card-sm">
      <div className="hero-media">
        <button type="button" className="hero-img-btn" onClick={openDefault}>
          <Thumb
            name={card.recipe.name}
            imageUrl={card.recipe.imageUrl}
            meal={card.recipe.mealType}
            size={54}
            className="hero-img"
          />
          <span className="hero-grad" />
          <h3 className="hero-title">{card.recipe.name}</h3>
        </button>

        <div className="hero-actions">
          <Dropdown isOpen={menuOpen} onOpenChange={setMenuOpen}>
            <Dropdown.Trigger>
              <button type="button" className="rnd" aria-label="Opcions">
                <DotsIcon />
              </button>
            </Dropdown.Trigger>
            <Dropdown.Popover placement="bottom end">
              <Dropdown.Menu onAction={handleAction}>
                <Dropdown.Item id="cook" textValue="Cuina ara">
                  <Label>👨‍🍳 Cuina ara</Label>
                </Dropdown.Item>
                <Dropdown.Item id="view" textValue="Veure recepta">
                  <Label>Veure recepta</Label>
                </Dropdown.Item>
                <Dropdown.Item id="edit" textValue="Edita la recepta">
                  <Label>Edita la recepta</Label>
                </Dropdown.Item>
                <Dropdown.Item id="remove" variant="danger" textValue="Treu del dia">
                  <Label>Treu del dia</Label>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
          <button
            type="button"
            className="rnd"
            aria-label="Treu del dia"
            onClick={() => handlers.onDeleteCard(card.id)}
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      <div className="hero-foot">
        <div className="flex gap-3.5">
          <span className="hero-meta">
            <ClockIcon />
            {card.recipe.prepTimeMin || "—"} min
          </span>
          <span className="hero-meta">
            <UsersIcon />
            {servings} {servings === 1 ? "ració" : "racions"}
          </span>
        </div>
        <PeopleRow
          users={users}
          selected={selected}
          onToggle={(userId) => handlers.onToggleUser(card, userId)}
        />
      </div>
    </div>
  );
}

function PeopleRow({
  users,
  selected,
  onToggle,
}: {
  users: AppUser[];
  selected: number[];
  onToggle: (userId: number) => void;
}) {
  return (
    <div className="people">
      {users.map((u) => {
        const on = selected.includes(u.id);
        return (
          <button
            key={u.id}
            type="button"
            title={u.name}
            className={`person ${on ? "on" : "off"}`}
            style={{ ["--uc" as string]: u.avatarColor }}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(u.id);
            }}
          >
            <span className="pe">{u.avatarEmoji}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------- icones ---------- */
function ClockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16 19v-1a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v1" />
      <circle cx="9" cy="7" r="3.2" />
      <path d="M22 19v-1a4 4 0 0 0-3-3.8" />
      <path d="M16 3.5a4 4 0 0 1 0 7" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="12" cy="5" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="12" cy="19" r="2" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}
